import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../layout/Layout';
import type { TournamentDetail } from '../../hooks/useScoringTournament';
import type { Round2AdminView } from '../../lib/round2';
import { tournamentHasRound2 } from '../../lib/round2';
import { resultsCsv, resultsFileName } from '../../lib/results-csv';
import { scoringListPath } from '../../lib/scoring-routes';

/** Read-only results, embedded on management home or standalone on protected result pages. */
export function FinalTournamentView({ detail, round2, embedded = false }: { detail: TournamentDetail; round2?: Round2AdminView | null; embedded?: boolean }) {
  const { tournament } = detail;
  const rows = detail.leaderboard.rows.map((row) => {
    const shooter = detail.shooters.find((person) => person.id === row.shooterId);
    return {
      ...shooter,
      ...row,
      shots: row.shots ?? detail.scores.filter((score) => score.shooterId === row.shooterId && score.shotNumber > 0),
    };
  });
  const teams = detail.leaderboard.round1?.teams;
  const csv = resultsCsv(rows, tournament.shotsPlanned ?? 15, teams);
  const results = (
      <div className="mx-auto max-w-3xl space-y-5">
        {embedded ? null : <h1 className="text-2xl font-semibold">{tournament.title}</h1>}
        <p role="status" className="rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface-2)] p-4 font-semibold">
          Tournament finished — scores and teams are locked
        </p>
        <nav aria-label="Final results" className="flex flex-wrap gap-4 text-[var(--color-accent)]">
          <Link to={scoringListPath(tournament.slug)}>Final results</Link>
          <Link to={`/scoreboard/${encodeURIComponent(tournament.slug)}`}>Public leaderboard</Link>
          {tournamentHasRound2(tournament) ? <Link to={`${scoringListPath(tournament.slug)}/round2`}>Round 2 results</Link> : null}
          <a href={`data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`} download={resultsFileName(tournament.slug, 'all')}>Download results (CSV)</a>
        </nav>
        {teams?.length ? (
          <section aria-label="Round 1 team results" className="space-y-2">
            <h2 className="text-lg font-semibold">Round 1 teams</h2>
            <ul className="space-y-2">{teams.map((team) => <li key={team.teamId ?? team.name} className="flex justify-between gap-3"><span>{team.name}</span><span>{team.totalDisplay}</span></li>)}</ul>
          </section>
        ) : null}
        <section aria-label="Round 1 results" className="space-y-2">
          <h2 className="text-lg font-semibold">Round 1 results</h2>
          <ol className="space-y-2">
            {rows.map((row) => <li key={row.shooterId} className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3">
              <div className="flex justify-between gap-3"><span>{row.rank > 0 ? `${row.rank}. ` : ''}{row.name}{row.bib ? ` · ${row.bib}` : ''}{row.teamName ? ` · ${row.teamName}` : ''}</span><strong>{row.totalDisplay}</strong></div>
              {row.shots.length ? <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Shots: {row.shots.map((shot) => `${shot.shotNumber}: ${shot.pointsDisplay}${'counted' in shot && shot.counted === false ? ' (out)' : ''}`).join(' · ')}</p> : null}
            </li>)}
          </ol>
        </section>
        {round2 ? (
          <section aria-label="Round 2 results" className="space-y-3">
            <h2 className="text-lg font-semibold">Round 2 results</h2>
            <p className="text-sm">Ranked by Round 2 total.</p>
            <ol className="space-y-3">{round2.teamBoard.map((team) => <li key={team.teamId} className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3">
              <div className="flex justify-between gap-3"><strong>{team.rank > 0 ? `${team.rank}. ` : ''}{team.name}</strong><span>{team.totalDisplay}</span></div>
              <ul>{team.members.map((member) => <li key={member.shooterId} className="flex justify-between gap-3"><span>{member.name}</span><span>{member.totalDisplay}</span></li>)}</ul>
            </li>)}</ol>
          </section>
        ) : null}
      </div>
  );
  return embedded ? results : <Layout>{results}</Layout>;
}
